import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import KuutioCubeGradient from '../KuutioCubeGradient';
import {
  evaluate, stateFromTouch, screenOffset, DEFAULT_STATE, DEFAULT_GAPS,
  SCREEN_W, SCREEN_H, CUBE_SIZE,
} from './cubePhysicsMath';

const Stage = styled.div`
  position: absolute;
  inset: 0;
  background: #000;
  overflow: hidden;
  touch-action: none;
`;

const CubeCarrier = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  will-change: transform;
`;

/**
 * CubePhysicsView — this screen's window into the shared cube world.
 *
 * The cube's position is a pure function of server time (see
 * cubePhysicsMath.js): every wall evaluates the same state through its
 * clock-ping offset, so the cube crosses screen edges and room corners
 * seamlessly. Tapping steers it: critically damped to the tap x, bounce
 * apex easing to the tap y.
 *
 * Without a socket (designer preview tiles) it runs a local idle state.
 */
const CubePhysicsView = ({ screenCode, socket = null, clockOffset = 0, instanceId = null }) => {
  const stageRef = useRef(null);
  const carrierRef = useRef(null);
  const stateRef = useRef({ state: DEFAULT_STATE(Date.now() + clockOffset), gaps: DEFAULT_GAPS });
  const spinRef = useRef({ rate: 1, xRate: 0.85 }); // tumble follows movement; constant x-roll keeps it 3D
  const [scale, setScale] = useState(1); // stage px per world px

  // Live state from the server (or local idle in preview)
  useEffect(() => {
    if (!socket) return undefined;
    const onState = (payload) => {
      if (payload?.state) stateRef.current = { state: payload.state, gaps: payload.gaps || DEFAULT_GAPS };
    };
    socket.on('cube-state', onState);
    socket.emit('cube-state-request', { instanceId });
    return () => socket.off('cube-state', onState);
  }, [socket, instanceId]);

  // Stage scale: the stage may be a scaled preview tile; world units are
  // 1920-per-screen, so map via actual width
  useEffect(() => {
    const node = stageRef.current;
    if (!node) return undefined;
    const apply = () => setScale((node.clientWidth || SCREEN_W) / SCREEN_W);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // Render loop: evaluate world position, place the cube in this screen's window
  useEffect(() => {
    let rafId;
    const tick = () => {
      rafId = requestAnimationFrame(tick);
      const carrier = carrierRef.current;
      if (!carrier) return;
      const { state, gaps } = stateRef.current;
      const serverNow = Date.now() + clockOffset;
      const { x, y, vx } = evaluate(state, serverNow, gaps);
      // Tumble follows the movement: faster travel spins faster, direction
      // flips with travel direction; near-still keeps a gentle idle turn.
      // On a fresh tap the spin SURGES instantly and decays into the travel —
      // the "I noticed you" acknowledgment, since translation starts gently.
      const age = Math.max(0, (serverNow - state.t0) / 1000);
      const notice = state.mode === 'steer' ? 2.4 * Math.exp(-2.5 * age) : 0;
      spinRef.current.rate = (vx < 0 ? -1 : 1)
        * Math.min(0.35 + Math.abs(vx) / 160, 3.5) * (1 + notice);
      const offset = screenOffset(screenCode, gaps);
      const localX = x - offset;
      // Visible (with slack) on this screen?
      if (localX < -CUBE_SIZE || localX > SCREEN_W + CUBE_SIZE) {
        carrier.style.display = 'none';
        return;
      }
      carrier.style.display = 'block';
      const px = (localX - CUBE_SIZE / 2) * scale;
      const pyTop = (SCREEN_H - y - CUBE_SIZE) * scale;
      carrier.style.transform = `translate(${px}px, ${pyTop}px) scale(${scale})`;
    };
    tick();
    return () => cancelAnimationFrame(rafId);
  }, [screenCode, clockOffset, scale]);

  const handlePointerDown = (e) => {
    const node = stageRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const localX = ((e.clientX - rect.left) / rect.width) * SCREEN_W;
    const localY = (1 - (e.clientY - rect.top) / rect.height) * SCREEN_H; // y up from floor
    const { gaps } = stateRef.current;
    const worldX = screenOffset(screenCode, gaps) + localX;
    if (socket) {
      socket.emit('cube-touch', { instanceId, worldX, worldY: localY });
    } else {
      // Preview: steer locally so the designer can feel the mechanic
      const serverNow = Date.now() + clockOffset;
      stateRef.current = {
        ...stateRef.current,
        state: stateFromTouch(stateRef.current.state, serverNow, worldX, localY, stateRef.current.gaps),
      };
    }
  };

  return (
    <Stage ref={stageRef} onPointerDown={handlePointerDown}>
      <CubeCarrier ref={carrierRef} style={{ transformOrigin: 'top left' }}>
        <KuutioCubeGradient size={CUBE_SIZE} spinRef={spinRef} />
      </CubeCarrier>
    </Stage>
  );
};

export default CubePhysicsView;
